 
"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "next/navigation";
import { Loader2 } from "lucide-react";

import { ScirixLogoMark } from "@/components/branding/scirix-logo-mark";
import type { AuthProviderDescriptor } from "@/lib/auth-types";
import {
  fetchAuthProviders,
  beginAuthorize,
  OAUTH_RETURN_TO_KEY,
  OAUTH_INVITE_CODE_KEY,
  OAUTH_SIGNUP_KEY,
} from "@/lib/auth-client";
import { useBillingConfig } from "@/hooks/useBillingConfig";

interface SsoState {
  provider?: string;
  loading: boolean;
  error?: string | null;
}

interface ProvidersErrorState {
  summary: string;
  detail?: string;
}

const PROVIDER_ERROR_FALLBACK_MESSAGE =
  "Sign-in is temporarily unavailable. Try again in a moment or contact your administrator.";

function resolveProvidersErrorState(error: unknown): ProvidersErrorState {
  const rawMessage =
    error instanceof Error
      ? error.message
      : typeof error === "string"
        ? error
        : null;
  const detail = rawMessage?.trim();
  if (!detail) {
    return { summary: PROVIDER_ERROR_FALLBACK_MESSAGE };
  }
  if (detail.toLowerCase() === PROVIDER_ERROR_FALLBACK_MESSAGE.toLowerCase()) {
    return { summary: PROVIDER_ERROR_FALLBACK_MESSAGE };
  }
  return { summary: PROVIDER_ERROR_FALLBACK_MESSAGE, detail };
}

export default function LoginView() {
  const searchParams = useSearchParams();
  const returnTo = useMemo(() => {
    const raw = searchParams.get("returnTo");
    if (!raw || !raw.startsWith("/") || raw.startsWith("//")) return "/";
    return raw;
  }, [searchParams]);
  // An invite link (/login?invite=…) pre-fills the code; the field stays visible so
  // the recipient can see what will be redeemed when they sign in. Like returnTo,
  // the query param is available on first client render, so plain initial state works.
  const inviteParam = searchParams.get("invite")?.trim() ?? "";
  const [inviteCode, setInviteCode] = useState(inviteParam);
  const [showInviteField, setShowInviteField] = useState(inviteParam.length > 0);
  const [providers, setProviders] = useState<AuthProviderDescriptor[]>([]);
  const [loadingProviders, setLoadingProviders] = useState(true);
  const [providersError, setProvidersError] = useState<ProvidersErrorState | null>(null);
  const [showProvidersErrorDetail, setShowProvidersErrorDetail] = useState(false);
  const [ssoState, setSsoState] = useState<SsoState>({ loading: false });
  const [ssoAttemptId, setSsoAttemptId] = useState(0);
  // Self-signup exists only when the install has a billing provider
  // (docs/plan/stripe-subscriptions.md, "Sign-up"). In sign-up mode the same
  // provider buttons carry a `signup` intent across the OAuth round-trip.
  const { signupEnabled } = useBillingConfig();
  const [signupMode, setSignupMode] = useState(false);

  // Safety net: if the busy state has been stuck for too long (e.g. the
  // post-login navigation got bounced back to /login by the middleware because
  // the session cookie was rejected, or the exchange request never returned),
  // exit the spinner so the user can retry instead of staring at
  // "Completing Google sign-in…" indefinitely.
  useEffect(() => {
    if (!ssoState.loading) return;
    const timer = setTimeout(() => {
      setSsoState((state) =>
        state.loading
          ? {
              provider: state.provider,
              loading: false,
              error: "Sign-in didn't complete. Please try again.",
            }
          : state,
      );
    }, 8000);
    return () => clearTimeout(timer);
  }, [ssoState.loading, ssoAttemptId]);

  useEffect(() => {
    let cancelled = false;

    fetchAuthProviders()
      .then((result) => {
        if (cancelled) return;
        setProviders(result);
        setProvidersError(null);
        setShowProvidersErrorDetail(false);
      })
      .catch((error) => {
        if (cancelled) return;
        setProvidersError(resolveProvidersErrorState(error));
        setShowProvidersErrorDetail(false);
      })
      .finally(() => {
        if (!cancelled) {
          setLoadingProviders(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Every supported provider authenticates via the server-side redirect
  // (AuthorizationCode) flow; the actual session is established on the callback page.
  const signInProviders = useMemo(
    () => providers.filter((provider) => provider.credentialKind === "AuthorizationCode"),
    [providers],
  );

  const handleRedirectSignIn = useCallback(
    async (providerName: string) => {
      setSsoAttemptId((id) => id + 1);
      setSsoState({ provider: providerName, loading: true, error: null });
      try {
        // Stash returnTo (and any invite code) so the callback page can resume the
        // sign-in with them after the round-trip.
        if (typeof window !== "undefined") {
          window.sessionStorage.setItem(OAUTH_RETURN_TO_KEY, returnTo);
          const trimmedInvite = signupMode ? "" : inviteCode.trim();
          if (trimmedInvite) {
            window.sessionStorage.setItem(OAUTH_INVITE_CODE_KEY, trimmedInvite);
          } else {
            window.sessionStorage.removeItem(OAUTH_INVITE_CODE_KEY);
          }
          if (signupMode) {
            window.sessionStorage.setItem(OAUTH_SIGNUP_KEY, "1");
          } else {
            window.sessionStorage.removeItem(OAUTH_SIGNUP_KEY);
          }
        }
        const { authorizeUrl } = await beginAuthorize(providerName);
        window.location.assign(authorizeUrl);
      } catch (error) {
        const message = error instanceof Error ? error.message : "Could not start sign-in.";
        setSsoState({ provider: providerName, loading: false, error: message });
      }
    },
    [returnTo, inviteCode, signupMode],
  );

  return (
    <main
      className="relative min-h-screen overflow-hidden text-foreground"
      style={{
        backgroundImage: "var(--token-background-gradient)",
        backgroundColor: "var(--token-foundation-background-canvas)",
      }}
    >
      <div
        className="pointer-events-none absolute inset-0"
        aria-hidden
        style={{
          background: "color-mix(in oklab, var(--token-foundation-surface-base) 72%, transparent)",
        }}
      />
      <div className="relative flex min-h-screen w-full items-center justify-center px-4 py-12 sm:px-8">
        <div className="w-full max-w-[28rem] space-y-8 rounded-[var(--radius-giga)] border border-[color-mix(in_oklab,var(--token-border-default)_82%,transparent)] bg-[color-mix(in_oklab,var(--token-foundation-surface-base)_84%,transparent)] p-8 shadow-[var(--shadow-lg)] sm:p-10">
          <ScirixBranding />
          <div className="space-y-3 text-center">
            <h1 className="text-3xl font-semibold tracking-tight sm:text-4xl">Scirix</h1>
            <p className="text-base text-muted-foreground">
              {signupMode ? "Create an organization." : "Continuous reality model."}
            </p>
          </div>

          {signupMode ? (
            <div
              className="space-y-2 rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-border-default)_70%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_82%,transparent)] px-4 py-4 text-left"
              role="region"
              aria-label="About creating an organization"
            >
              <p className="text-sm font-medium text-foreground">Scirix is sold to businesses.</p>
              <p className="text-sm text-muted-foreground">
                Sign in with a provider below to create an organization for your company. You will then
                pick a plan and pay by card. Checkout asks for your company name, billing address and VAT
                id. Prices are in euros and billed monthly.
              </p>
              <p className="text-xs text-muted-foreground">
                Already have an account? Use the same provider buttons after going back to sign-in.
              </p>
            </div>
          ) : null}

          {providersError && (
            <div
              className="space-y-3 rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-surface-danger)_55%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-danger)_22%,transparent)] px-4 py-4"
              role="alert"
            >
              <p className="text-sm font-medium text-[color:var(--token-surface-danger)]">
                {providersError.summary}
              </p>
              {providersError.detail ? (
                <div className="space-y-2 text-xs">
                  <button
                    type="button"
                    className="text-left font-semibold uppercase tracking-[0.2em] text-[color:var(--token-foreground-default)] hover:text-[color:var(--token-surface-danger)]"
                    onClick={() => setShowProvidersErrorDetail((previous) => !previous)}
                    aria-expanded={showProvidersErrorDetail}
                  >
                    {showProvidersErrorDetail ? "Hide technical detail" : "Show technical detail"}
                  </button>
                  {showProvidersErrorDetail && (
                    <p className="rounded-[var(--radius-xl)] border border-[color-mix(in_oklab,var(--token-border-default)_45%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_55%,transparent)] p-3 text-[color:var(--token-foreground-default)]">
                      {providersError.detail}
                    </p>
                  )}
                </div>
              ) : null}
            </div>
          )}

          {loadingProviders ? (
            <div
              className="flex items-center justify-center gap-3 rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-border-default)_70%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_82%,transparent)] py-8 text-sm text-muted-foreground"
              role="status"
            >
              <Loader2 className="h-5 w-5 animate-spin text-foreground" />
              Loading sign-in…
            </div>
          ) : signInProviders.length > 0 ? (
            <div className="space-y-4">
              {signInProviders.map((provider) => (
                <SsoProviderCard
                  key={provider.name}
                  provider={provider}
                  busy={ssoState.loading && ssoState.provider === provider.name}
                  error={ssoState.provider === provider.name ? ssoState.error : null}
                  onRedirect={handleRedirectSignIn}
                />
              ))}

              {signupMode ? (
                <button
                  type="button"
                  onClick={() => setSignupMode(false)}
                  className="w-full text-center text-sm text-muted-foreground transition hover:text-foreground"
                >
                  Back to sign-in
                </button>
              ) : signupEnabled ? (
                <div className="space-y-2 rounded-[var(--radius-xxl)] border border-dashed border-[color-mix(in_oklab,var(--token-border-default)_65%,transparent)] px-4 py-4 text-left">
                  <p className="text-sm font-medium text-foreground">New to Scirix?</p>
                  <p className="text-xs text-muted-foreground">
                    Scirix is sold to businesses. Create an organization for your company and pick a plan.
                  </p>
                  <button
                    type="button"
                    onClick={() => setSignupMode(true)}
                    className="w-full rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-border-default)_78%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_92%,transparent)] px-5 py-2.5 text-sm font-medium text-foreground shadow-[var(--shadow-sm)] transition hover:bg-[color-mix(in_oklab,var(--token-surface-default)_80%,transparent)]"
                  >
                    Create an organization
                  </button>
                </div>
              ) : null}

              {signupMode ? null : showInviteField ? (
                <div className="space-y-2 text-left">
                  <label htmlFor="inviteCode" className="text-sm font-medium text-foreground">
                    Invite code
                  </label>
                  <input
                    id="inviteCode"
                    type="text"
                    autoComplete="off"
                    spellCheck={false}
                    value={inviteCode}
                    onChange={(event) => setInviteCode(event.target.value)}
                    placeholder="Paste your invite code"
                    className="w-full rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-border-default)_78%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_92%,transparent)] px-4 py-2.5 font-mono text-sm text-foreground shadow-[var(--shadow-sm)] outline-none transition placeholder:font-sans placeholder:text-muted-foreground focus:border-[color-mix(in_oklab,var(--token-foreground-default)_55%,transparent)]"
                  />
                  <p className="text-xs text-muted-foreground">
                    The code is redeemed automatically when you continue with a provider above.
                  </p>
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowInviteField(true)}
                  className="w-full text-center text-sm text-muted-foreground transition hover:text-foreground"
                >
                  Have an invite code?
                </button>
              )}
            </div>
          ) : (
            !providersError && (
              <div className="rounded-[var(--radius-xxl)] border border-dashed border-[color-mix(in_oklab,var(--token-border-default)_65%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_78%,transparent)] px-4 py-4 text-sm text-muted-foreground">
                Sign-in isn’t available yet. Contact your administrator to enable it.
              </div>
            )
          )}
        </div>
      </div>
    </main>
  );
}

function ScirixBranding() {
  return (
    <div className="flex items-center justify-center text-foreground">
      <ScirixLogoMark className="h-24 w-auto" />
    </div>
  );
}

interface SsoProviderCardProps {
  provider: AuthProviderDescriptor;
  busy: boolean;
  error: string | null | undefined;
  onRedirect: (providerName: string) => void;
}

function SsoProviderCard({ provider, busy, error, onRedirect }: SsoProviderCardProps) {
  const baseCardClass =
    "rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-border-default)_78%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_90%,transparent)] p-6 shadow-[var(--shadow-sm)]";

  if (provider.credentialKind === "AuthorizationCode") {
    return (
      <div className="space-y-3">
        <RedirectSignInButton provider={provider} busy={busy} onRedirect={onRedirect} />
        {busy && (
          <p className="text-center text-xs text-muted-foreground">
            Redirecting to {provider.displayName}…
          </p>
        )}
        {error && <p className="text-center text-sm text-[color:var(--token-surface-danger)]">{error}</p>}
      </div>
    );
  }

  return (
    <div className={baseCardClass}>
      <p className="text-base font-medium text-foreground">{provider.displayName}</p>
      <p className="text-sm text-muted-foreground">
        Sign-in with this provider isn’t supported yet.
      </p>
    </div>
  );
}

interface RedirectSignInButtonProps {
  provider: AuthProviderDescriptor;
  busy: boolean;
  onRedirect: (providerName: string) => void;
}

function RedirectSignInButton({ provider, busy, onRedirect }: RedirectSignInButtonProps) {
  return (
    <button
      type="button"
      disabled={busy}
      onClick={() => onRedirect(provider.name)}
      className="flex w-full items-center justify-center gap-3 rounded-[var(--radius-xxl)] border border-[color-mix(in_oklab,var(--token-border-default)_78%,transparent)] bg-[color-mix(in_oklab,var(--token-surface-default)_92%,transparent)] px-5 py-3 text-base font-medium text-foreground shadow-[var(--shadow-sm)] transition hover:bg-[color-mix(in_oklab,var(--token-surface-default)_80%,transparent)] disabled:cursor-not-allowed disabled:opacity-60"
      aria-busy={busy}
    >
      {busy ? (
        <Loader2 className="h-5 w-5 animate-spin" />
      ) : (
        <ProviderGlyph name={provider.name} />
      )}
      <span>Continue with {provider.displayName}</span>
    </button>
  );
}

// Monochrome brand marks that inherit the button's foreground color (fill-current), keeping
// us on the design-system palette rather than hard-coding each provider's brand hues.
function ProviderGlyph({ name }: { name: string }) {
  const normalized = name.toLowerCase();
  if (normalized === "google") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
        <path d="M21.35 11.1H12v3.83h5.35a4.58 4.58 0 0 1-1.98 3.01v2.5h3.2c1.87-1.72 2.95-4.26 2.95-7.26 0-.68-.06-1.34-.17-1.97v-.14z" opacity="0.9" />
        <path d="M12 22c2.7 0 4.96-.9 6.62-2.43l-3.2-2.5c-.89.6-2.03.95-3.42.95-2.63 0-4.86-1.78-5.66-4.17H3.04v2.6A10 10 0 0 0 12 22z" opacity="0.7" />
        <path d="M6.34 13.85a6 6 0 0 1 0-3.7V7.56H3.04a10 10 0 0 0 0 8.9l3.3-2.6z" opacity="0.55" />
        <path d="M12 6.58c1.47 0 2.8.51 3.84 1.5l2.84-2.84A10 10 0 0 0 3.04 7.56l3.3 2.6C7.14 7.76 9.37 6.58 12 6.58z" />
      </svg>
    );
  }
  if (normalized === "microsoft") {
    return (
      <svg viewBox="0 0 21 21" className="h-5 w-5 fill-current" aria-hidden>
        <rect x="1" y="1" width="9" height="9" />
        <rect x="11" y="1" width="9" height="9" opacity="0.75" />
        <rect x="1" y="11" width="9" height="9" opacity="0.75" />
        <rect x="11" y="11" width="9" height="9" opacity="0.5" />
      </svg>
    );
  }
  if (normalized === "github") {
    return (
      <svg viewBox="0 0 16 16" className="h-5 w-5 fill-current" aria-hidden>
        <path d="M8 0C3.58 0 0 3.58 0 8c0 3.54 2.29 6.53 5.47 7.59.4.07.55-.17.55-.38 0-.19-.01-.82-.01-1.49-2.01.37-2.53-.49-2.69-.94-.09-.23-.48-.94-.82-1.13-.28-.15-.68-.52-.01-.53.63-.01 1.08.58 1.23.82.72 1.21 1.87.87 2.33.66.07-.52.28-.87.51-1.07-1.78-.2-3.64-.89-3.64-3.95 0-.87.31-1.59.82-2.15-.08-.2-.36-1.02.08-2.12 0 0 .67-.21 2.2.82a7.5 7.5 0 0 1 4 0c1.53-1.04 2.2-.82 2.2-.82.44 1.1.16 1.92.08 2.12.51.56.82 1.27.82 2.15 0 3.07-1.87 3.75-3.65 3.95.29.25.54.73.54 1.48 0 1.07-.01 1.93-.01 2.2 0 .21.15.46.55.38A8.01 8.01 0 0 0 16 8c0-4.42-3.58-8-8-8Z" />
      </svg>
    );
  }
  if (normalized === "gitlab") {
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-current" aria-hidden>
        <path d="m12 21.42 3.68-11.33H8.32L12 21.42zm0 0L8.32 10.09H3.16L12 21.42zm-8.84-11.33L2.04 13.53a.76.76 0 0 0 .28.85L12 21.42 3.16 10.09zm0 0h5.16L6.1 3.26a.38.38 0 0 0-.72 0L3.16 10.09zM12 21.42 15.68 10.09h5.16L12 21.42zm8.84-11.33L21.96 13.53a.76.76 0 0 1-.28.85L12 21.42l8.84-11.33zm0 0h-5.16l2.22-6.83a.38.38 0 0 1 .72 0l2.22 6.83z" />
      </svg>
    );
  }
  if (normalized === "saml") {
    // Generic federated-SSO shield — SAML has no brand mark.
    return (
      <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
        <path d="M12 3 5 6v5c0 4.2 2.8 7.6 7 9 4.2-1.4 7-4.8 7-9V6l-7-3Z" />
        <path d="m9.2 11.8 1.9 1.9 3.7-3.8" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 24 24" className="h-5 w-5 fill-none stroke-current" strokeWidth="2" aria-hidden>
      <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4" />
    </svg>
  );
}

